import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store/store';
import { showNotification } from '../../store/slices/notificationSlice';
import * as noteTemplateService from '../../services/noteTemplateService';

interface NoteTemplatesAdminModalProps {
  open: boolean;
  onClose: () => void;
}

interface TemplateRowState {
  id: string;
  savedLabel: string;
  savedBody: string;
  label: string;
  body: string;
  saving: boolean;
  deleting: boolean;
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string | string[] } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string' && message.length > 0) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const NoteTemplatesAdminModal: React.FC<NoteTemplatesAdminModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [rows, setRows] = useState<TemplateRowState[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await noteTemplateService.list();
      setRows(
        data.map(t => ({
          id: t.id,
          savedLabel: t.label,
          savedBody: t.body,
          label: t.label,
          body: t.body,
          saving: false,
          deleting: false,
        })),
      );
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    void fetchTemplates();
    setNewLabel('');
    setNewBody('');
  }, [open]);

  const updateRow = (id: string, patch: Partial<TemplateRowState>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleCreate = async () => {
    if (creating) return;
    const trimmedLabel = newLabel.trim();
    if (trimmedLabel.length === 0) {
      dispatch(showNotification({ message: 'Label cannot be empty.', type: 'error' }));
      return;
    }
    setCreating(true);
    try {
      const created = await noteTemplateService.create({ label: trimmedLabel, body: newBody });
      setRows(prev => [
        {
          id: created.id,
          savedLabel: created.label,
          savedBody: created.body,
          label: created.label,
          body: created.body,
          saving: false,
          deleting: false,
        },
        ...prev,
      ]);
      setNewLabel('');
      setNewBody('');
      dispatch(showNotification({ message: 'Template created.', type: 'success' }));
    } catch (err) {
      dispatch(
        showNotification({
          message: extractErrorMessage(err, 'Failed to create template.'),
          type: 'error',
        }),
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (id: string) => {
    const row = rows.find(r => r.id === id);
    if (!row || row.saving || row.deleting) return;
    const trimmedLabel = row.label.trim();
    if (trimmedLabel.length === 0) {
      dispatch(showNotification({ message: 'Label cannot be empty.', type: 'error' }));
      return;
    }
    updateRow(id, { saving: true });
    try {
      const updated = await noteTemplateService.update(id, { label: trimmedLabel, body: row.body });
      updateRow(id, {
        saving: false,
        savedLabel: updated.label,
        savedBody: updated.body,
        label: updated.label,
        body: updated.body,
      });
      dispatch(showNotification({ message: 'Template saved.', type: 'success' }));
    } catch (err) {
      updateRow(id, { saving: false });
      dispatch(
        showNotification({
          message: extractErrorMessage(err, 'Failed to save template.'),
          type: 'error',
        }),
      );
    }
  };

  const handleDelete = async (id: string) => {
    const row = rows.find(r => r.id === id);
    if (!row || row.saving || row.deleting) return;
    updateRow(id, { deleting: true });
    try {
      await noteTemplateService.remove(id);
      setRows(prev => prev.filter(r => r.id !== id));
      dispatch(showNotification({ message: 'Template deleted.', type: 'success' }));
    } catch (err) {
      updateRow(id, { deleting: false });
      dispatch(
        showNotification({
          message: extractErrorMessage(err, 'Failed to delete template.'),
          type: 'error',
        }),
      );
    }
  };

  const hasDirtyRow = rows.some(r => r.label !== r.savedLabel || r.body !== r.savedBody);

  const handleClose = () => {
    if (hasDirtyRow) {
      if (!window.confirm('You have unsaved changes. Discard them?')) return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>Manage note templates</DialogTitle>
      <DialogContent>
        {loadError && (
          <Typography color='error' variant='body2' sx={{ mb: 2 }}>
            {loadError}
          </Typography>
        )}

        {loading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* New row form */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label='Label'
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                size='small'
                inputProps={{ maxLength: 64 }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                label='Body'
                value={newBody}
                onChange={e => setNewBody(e.target.value)}
                multiline
                maxRows={3}
                fullWidth
                inputProps={{ maxLength: 4000 }}
              />
              <Button variant='outlined' onClick={handleCreate} disabled={creating} sx={{ whiteSpace: 'nowrap' }}>
                {creating ? <CircularProgress size={20} /> : 'Create'}
              </Button>
            </Box>

            <Divider />

            {/* Existing rows */}
            {rows.map(row => {
              const isDirty = row.label !== row.savedLabel || row.body !== row.savedBody;
              return (
                <Box key={row.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label='Label'
                    value={row.label}
                    onChange={e => updateRow(row.id, { label: e.target.value })}
                    size='small'
                    inputProps={{ maxLength: 64 }}
                    sx={{ minWidth: 180 }}
                    disabled={row.saving || row.deleting}
                  />
                  <TextField
                    label='Body'
                    value={row.body}
                    onChange={e => updateRow(row.id, { body: e.target.value })}
                    multiline
                    maxRows={6}
                    fullWidth
                    inputProps={{ maxLength: 4000 }}
                    disabled={row.saving || row.deleting}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size='small'
                      onClick={() => handleSave(row.id)}
                      disabled={!isDirty || row.saving || row.deleting}
                      color='primary'
                    >
                      {row.saving ? <CircularProgress size={16} /> : <SaveIcon fontSize='small' />}
                    </IconButton>
                    <IconButton size='small' onClick={() => handleDelete(row.id)} disabled={row.saving || row.deleting} color='error'>
                      {row.deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize='small' />}
                    </IconButton>
                  </Box>
                </Box>
              );
            })}

            {rows.length === 0 && !loading && (
              <Typography variant='body2' color='text.secondary'>
                No templates yet. Create one above.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteTemplatesAdminModal;
